import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { ClientOnly, createFileRoute } from "@tanstack/react-router"
import { useServerFn } from "@tanstack/react-start"
import {
  type ColumnDef,
  createColumnHelper,
  type VisibilityState,
} from "@tanstack/react-table"
import {
  CheckCircleIcon,
  PencilIcon,
  Plus,
  Trash2Icon,
  XCircleIcon,
} from "lucide-react"
import { useMemo, useState } from "react"

import { ActionsDropdownMenu } from "@repo/ui/components/ui/actions-dropdown-menu"
import { Badge } from "@repo/ui/components/ui/badge"
import { Button } from "@repo/ui/components/ui/button"
import { DataTable } from "@repo/ui/components/ui/data-table"
import { toast } from "@repo/ui/components/ui/sonner"
import { useLocalStorage } from "@repo/ui/hooks/use-local-storage"
import { toDisplayCurrency } from "~/lib/utils/localization"
import { AddClientRateModal } from "~/modules/projects/components/add-client-rate-modal"
import { DeleteClientRateModal } from "~/modules/projects/components/delete-client-rate-modal"
import { EditClientRateModal } from "~/modules/projects/components/edit-client-rate-modal"
import {
  deleteProjectClientRateFn,
  getClientRateUsageCountFn,
  updateProjectClientRateFn,
} from "~/modules/projects/projects.functions"
import {
  getExtendedProjectQueryOptions,
  getProjectClientRatesQueryOptions,
} from "~/modules/projects/projects.queries"
import type { ProjectClientRate } from "~/modules/projects/projects.service"
import { useLanguageOptions } from "~/modules/system-config/hooks/use-language-options"
import { useSystemInvoiceItems } from "~/modules/system-config/hooks/use-system-invoice-items"
import { ComponentErrorBoundary } from "~/root-components/component-error-boundary"

export const Route = createFileRoute(
  "/_protected-layout/_os-layout/projects/$projectId/client-rates",
)({
  component: RouteComponent,
})

const columnHelper = createColumnHelper<ProjectClientRate>()

const TABLE_VISIBLE_COLUMNS_KEY = "project-client-rates-visible-columns"

function RouteComponent() {
  const params = Route.useParams()
  const queryClient = useQueryClient()

  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedClientRate, setSelectedClientRate] =
    useState<ProjectClientRate | null>(null)
  const [rateUsageCount, setRateUsageCount] = useState<number | undefined>(
    undefined,
  )

  const [visibleColumns, setVisibleColumns] = useLocalStorage<VisibilityState>(
    TABLE_VISIBLE_COLUMNS_KEY,
    {},
  )

  const toastId = "client-rate-action"

  const { data: project } = useSuspenseQuery(
    getExtendedProjectQueryOptions({ projectId: params.projectId }),
  )

  const { data: clientRates = [] } = useQuery(
    getProjectClientRatesQueryOptions({ projectId: params.projectId }),
  )
  const { getInvoiceItem } = useSystemInvoiceItems()
  const { toDisplayLanguage } = useLanguageOptions()

  // Mutations
  const updateClientRate = useServerFn(updateProjectClientRateFn)
  const deleteClientRate = useServerFn(deleteProjectClientRateFn)
  const getClientRateUsageCount = useServerFn(getClientRateUsageCountFn)

  const updateClientRateStatusMutation = useMutation({
    mutationFn: updateClientRate,
    meta: {
      invalidates: [
        getProjectClientRatesQueryOptions({ projectId: params.projectId })
          .queryKey,
      ],
    },
    onMutate: (variables) => {
      const newStatus =
        variables.data.clientRate.status === "active"
          ? "activating"
          : "deactivating"
      toast.loading(
        `${newStatus.charAt(0).toUpperCase() + newStatus.slice(1)} client rate...`,
        {
          id: toastId,
        },
      )
    },
    onSuccess: async (result) => {
      if (!result?.success) {
        toast.error(result?.message || "Failed to update client rate status", {
          id: toastId,
        })
        return
      }
      toast.success("Client rate status updated", { id: toastId })

      await queryClient.refetchQueries({
        queryKey: getProjectClientRatesQueryOptions({
          projectId: params.projectId,
        }).queryKey,
      })
    },
    onError: (error) => {
      toast.error("Error updating client rate status", {
        id: toastId,
        description: error.message,
      })
    },
  })

  const deleteClientRateMutation = useMutation({
    mutationFn: deleteClientRate,
    meta: {
      invalidates: [
        getProjectClientRatesQueryOptions({ projectId: params.projectId })
          .queryKey,
      ],
    },
    onMutate: () => {
      toast.loading("Deleting client rate...", { id: toastId })
    },
    onSuccess: async (result) => {
      if (!result?.success) {
        toast.error(result?.message || "Failed to delete client rate", {
          id: toastId,
        })
        return
      }
      toast.success("Client rate deleted", { id: toastId })

      await queryClient.refetchQueries({
        queryKey: getProjectClientRatesQueryOptions({
          projectId: params.projectId,
        }).queryKey,
      })
    },
    onError: (error) => {
      toast.error("Error deleting client rate", {
        id: toastId,
        description: error.message,
      })
    },
  })

  // biome-ignore lint/suspicious/noExplicitAny: please
  const columns: ColumnDef<ProjectClientRate, any>[] = useMemo(
    () => [
      columnHelper.display({
        id: "invoice_item",
        header: () => <div className="min-w-[180px]">Invoice Item</div>,
        cell: (info) => {
          const invoiceItem = getInvoiceItem(info.row.original.invoice_item_id)
          return invoiceItem
            ? invoiceItem.name
            : info.row.original.invoice_item_id
        },
        meta: {
          columnName: "Invoice Item",
        },
      }),
      columnHelper.accessor("locale", {
        id: "locale",
        header: () => <div className="min-w-[100px]">Language</div>,
        cell: (info) => toDisplayLanguage(info.row.original.locale),
        meta: {
          columnName: "Language",
        },
      }),
      columnHelper.display({
        id: "rate",
        header: () => <div>Client Rate</div>,
        cell: (info) => (
          <ClientOnly>
            <span>
              {toDisplayCurrency(
                Number(info.row.original.rate),
                info.row.original.currency,
                {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2,
                },
              )}
            </span>
          </ClientOnly>
        ),
        meta: {
          columnName: "Client Rate",
        },
      }),
      columnHelper.display({
        id: "currency",
        header: () => <div>Currency</div>,
        cell: (info) => info.row.original.currency || "-",
      }),
      columnHelper.accessor("status", {
        id: "status",
        header: () => <div className="w-[100px]">Status</div>,
        cell: (info) => (
          <Badge
            variant={
              info.row.original.status === "active" ? "default" : "secondary"
            }
          >
            {info.getValue()}
          </Badge>
        ),
        meta: {
          filterVariant: "select",
          filterSelectOptions: [
            {
              label: "Active",
              value: "active",
            },
            {
              label: "Inactive",
              value: "inactive",
            },
          ],
        },
      }),
      columnHelper.display({
        id: "actions",
        header: () => <div className="grow text-right">Actions</div>,
        enableHiding: false,
        cell: ({ row }) => {
          const isActive = row.original.status === "active"

          return (
            <div className="flex justify-end">
              <ActionsDropdownMenu
                groups={[
                  {
                    actions: [
                      {
                        label: "Edit Rate",
                        icon: PencilIcon,
                        onClick: () => {
                          setSelectedClientRate(row.original)
                          setShowEditModal(true)
                        },
                      },
                      {
                        label: isActive ? "Deactivate Rate" : "Activate Rate",
                        icon: isActive ? XCircleIcon : CheckCircleIcon,
                        onClick: () => {
                          updateClientRateStatusMutation.mutate({
                            data: {
                              projectClientRateId: row.original.id,
                              clientRate: {
                                invoice_item_id: row.original.invoice_item_id,
                                locale: row.original.locale,
                                currency: row.original.currency,
                                rate: row.original.rate,
                                status: isActive ? "inactive" : "active",
                              },
                            },
                          })
                        },
                      },
                    ],
                  },
                  {
                    actions: [
                      {
                        label: "Delete Rate",
                        icon: Trash2Icon,
                        variant: "destructive",
                        onClick: async () => {
                          setSelectedClientRate(row.original)
                          setRateUsageCount(undefined)
                          setShowDeleteModal(true)

                          // Fetch usage count in the background
                          const result = await getClientRateUsageCount({
                            data: {
                              projectClientRateId: row.original.id,
                            },
                          })

                          if (
                            result?.success &&
                            result.data?.count !== undefined
                          ) {
                            setRateUsageCount(result.data.count)
                          }
                        },
                      },
                    ],
                  },
                ]}
              />
            </div>
          )
        },
      }),
    ],
    [
      getInvoiceItem,
      toDisplayLanguage,
      updateClientRateStatusMutation,
      getClientRateUsageCount,
    ],
  )

  return (
    <div className="flex h-full flex-col">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-semibold text-lg">Client Rates</h3>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowAddModal(true)}
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Client Rate
        </Button>
      </div>
      <div>
        <DataTable
          data={clientRates}
          columns={columns}
          searchPlaceholder="Search client rates..."
          noResultsMessage="No client rates found"
          manualPagination={false}
          manualSorting={false}
          manualFiltering={false}
          getInitialColumnVisibility={() => visibleColumns}
          onColumnVisibilityChange={setVisibleColumns}
        />
      </div>

      <ClientOnly>
        <ComponentErrorBoundary>
          <AddClientRateModal
            key={params.projectId}
            open={showAddModal}
            onOpenChange={setShowAddModal}
            projectId={params.projectId}
            clientUnitId={project.client_unit_id}
            businessEntityId={project.business_entity_id}
          />

          {selectedClientRate && (
            <EditClientRateModal
              key={selectedClientRate.id}
              open={showEditModal}
              onOpenChange={setShowEditModal}
              clientRate={selectedClientRate}
              businessEntityId={project.business_entity_id}
            />
          )}

          {selectedClientRate && (
            <DeleteClientRateModal
              key={`delete-${selectedClientRate.id}`}
              open={showDeleteModal}
              onOpenChange={setShowDeleteModal}
              usageCount={rateUsageCount}
              rateDetails={{
                invoiceItemName:
                  getInvoiceItem(selectedClientRate.invoice_item_id)?.name ||
                  selectedClientRate.invoice_item_id,
                language: toDisplayLanguage(selectedClientRate.locale),
                rate: toDisplayCurrency(
                  Number(selectedClientRate.rate),
                  selectedClientRate.currency,
                  {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                  },
                ),
                currency: selectedClientRate.currency,
              }}
              onDeleteConfirmed={() =>
                deleteClientRateMutation.mutate(
                  {
                    data: {
                      projectClientRateId: selectedClientRate.id,
                    },
                  },
                  {
                    onSuccess() {
                      setSelectedClientRate(null)
                      setShowDeleteModal(false)
                      setRateUsageCount(undefined)
                    },
                  },
                )
              }
              isDeleting={deleteClientRateMutation.isPending && showDeleteModal}
            />
          )}
        </ComponentErrorBoundary>
      </ClientOnly>
    </div>
  )
}
